"use client";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ConnectionConfig } from "../connection-studio";

interface AdvancedTabProps {
  formData: ConnectionConfig;
  onChange: (updates: Partial<ConnectionConfig>) => void;
}

export function AdvancedTab({ formData, onChange }: AdvancedTabProps) {
  const config = formData.config || {};

  const updateConfig = (key: string, value: unknown) => {
    onChange({
      config: { ...config, [key]: value },
    });
  };

  return (
    <div className="space-y-8">
      {/* Timeout Settings */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-medium text-white">Timeout Settings</h3>
          <p className="text-sm text-white/50">Configure request timeout behavior</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-white/80">Request Timeout (seconds)</Label>
            <Input
              type="number"
              value={(config.timeout as number) || 30}
              onChange={(e) => updateConfig("timeout", parseInt(e.target.value) || 30)}
              min={1}
              max={300}
              className="border-white/10 bg-white/5 text-white"
            />
            <p className="text-xs text-white/40">
              Maximum time to wait for a response
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-white/80">Connect Timeout (seconds)</Label>
            <Input
              type="number"
              value={(config.connect_timeout as number) || 10}
              onChange={(e) => updateConfig("connect_timeout", parseInt(e.target.value) || 10)}
              min={1}
              max={60}
              className="border-white/10 bg-white/5 text-white"
            />
            <p className="text-xs text-white/40">
              Maximum time to establish connection
            </p>
          </div>
        </div>
      </div>

      {/* SSL Settings */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-medium text-white">SSL/TLS Settings</h3>
          <p className="text-sm text-white/50">Configure secure connection options</p>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-4">
          <div>
            <Label className="text-white/80">Verify SSL Certificate</Label>
            <p className="text-xs text-white/40">
              Disable only for self-signed certificates in development
            </p>
          </div>
          <Switch
            checked={(config.verify_ssl as boolean) !== false}
            onCheckedChange={(checked) => updateConfig("verify_ssl", checked)}
          />
        </div>
      </div>

      {/* Retry Settings */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-medium text-white">Retry Settings</h3>
          <p className="text-sm text-white/50">Configure automatic retry on failure</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-white/80">Max Retries</Label>
            <Input
              type="number"
              value={(config.max_retries as number) || 3}
              onChange={(e) => updateConfig("max_retries", parseInt(e.target.value) || 3)}
              min={0}
              max={10}
              className="border-white/10 bg-white/5 text-white"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/80">Retry Delay (seconds)</Label>
            <Input
              type="number"
              value={(config.retry_delay as number) || 1}
              onChange={(e) => updateConfig("retry_delay", parseFloat(e.target.value) || 1)}
              min={0.1}
              max={60}
              step={0.1}
              className="border-white/10 bg-white/5 text-white"
            />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-4">
          <div>
            <Label className="text-white/80">Exponential Backoff</Label>
            <p className="text-xs text-white/40">
              Double the delay after each retry attempt
            </p>
          </div>
          <Switch
            checked={(config.exponential_backoff as boolean) || false}
            onCheckedChange={(checked) => updateConfig("exponential_backoff", checked)}
          />
        </div>
      </div>

      {/* Rate Limiting */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-medium text-white">Rate Limiting</h3>
          <p className="text-sm text-white/50">Prevent hitting API rate limits</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-white/80">Requests Per Second</Label>
            <Input
              type="number"
              value={(config.rate_limit as number) || 0}
              onChange={(e) => updateConfig("rate_limit", parseInt(e.target.value) || 0)}
              min={0}
              max={1000}
              className="border-white/10 bg-white/5 text-white"
            />
            <p className="text-xs text-white/40">
              Set to 0 for no limit
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-white/80">Burst Limit</Label>
            <Input
              type="number"
              value={(config.burst_limit as number) || 0}
              onChange={(e) => updateConfig("burst_limit", parseInt(e.target.value) || 0)}
              min={0}
              max={100}
              className="border-white/10 bg-white/5 text-white"
            />
            <p className="text-xs text-white/40">
              Maximum requests in a burst
            </p>
          </div>
        </div>
      </div>

      {/* Response Handling */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-medium text-white">Response Handling</h3>
          <p className="text-sm text-white/50">Configure how responses are processed</p>
        </div>

        <div className="space-y-2">
          <Label className="text-white/80">Response Format</Label>
          <Select
            value={(config.response_format as string) || "auto"}
            onValueChange={(value) => updateConfig("response_format", value)}
          >
            <SelectTrigger className="border-white/10 bg-white/5 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto-detect</SelectItem>
              <SelectItem value="json">JSON</SelectItem>
              <SelectItem value="xml">XML</SelectItem>
              <SelectItem value="text">Plain Text</SelectItem>
              <SelectItem value="binary">Binary</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-4">
          <div>
            <Label className="text-white/80">Follow Redirects</Label>
            <p className="text-xs text-white/40">
              Automatically follow HTTP redirects (3xx responses)
            </p>
          </div>
          <Switch
            checked={(config.follow_redirects as boolean) !== false}
            onCheckedChange={(checked) => updateConfig("follow_redirects", checked)}
          />
        </div>
      </div>
    </div>
  );
}
